import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/solid";
import {
  ArrowUpOnSquareIcon,
  ArrowDownOnSquareIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/solid";
import { useBeamTrussStore } from "../../../hooks/useBeamTrussManagement";
import PredefinedLoadManager from "../../loading/PredefinedLoadManager";
import { useTrussStore } from "../../../hooks/useTrussManagement";
import { useModelStore } from "../../../store/useModelStore";
import {
  TableLayout,
  TableColumn,
} from "../../workspace/components/ui/TableLayout";
import { CollapsiblePanel } from "../../workspace/components/ui/CollapsiblePanel";
import ManualLoadInput from "../../ui/ManualLoadInput";
import {
  processExcelFile,
  importBeamLoadsFromCSV,
  exportBeamLoadsToCSV,
  importNodeLoadsFromCSV,
  exportNodeLoadsToCSV,
  copyBeamLoadsToClipboard,
  copyNodeLoadsToClipboard,
  detectLoadTypeFromHeaders,
} from "@/utilities/CSVUtils";
import {
  validateBeamLoadcaseReferences,
  validateDuplicateLoadcaseAssignments,
} from "@/utils/structuralValidation";

import { useWorkspaceState } from "../../../../src/components/workspace/hooks/core/useWorkspaceState";
import Papa from "papaparse";
import {
  useTableSelection,
  useCursorMode,
  useCancelAllApplyModes,
} from "@/hooks/useGlobalSelection";
import { useGlobalSelectionStore } from "@/store/useGlobalSelectionStore";
import { useLoadcaseGroupStore } from "@/store/useLoadcaseGroupStore";

type TrussType = "regular" | "perpendicular";

interface TrussPreset {
  id: string;
  name: string;
  type: TrussType;
  rotationValues: { X: number; Y: number; Z: number };
  perpendicularValues: { X: number; Y: number; Z: number };
  nodeIds?: string[];
}

const formatTrussTypeName = (type: TrussType): string => {
  return type.charAt(0).toUpperCase() + type.slice(1);
};

export const LoadcaseDataTable: React.FC = () => {
  const beamTrusses = useBeamTrussStore((s) => s.beamTrusses);
  const groupedTrussPresets = useTrussStore((s) => s.getGroupedTruss());

  const { selectedBeamIds = [], selectedNodeIds } = useModelStore();

  const { callbacks } = useWorkspaceState();

  // Track if terminal is executing (to skip auto-add during terminal execution)
  const isTerminalExecutingRef = useRef(false);

  // Listen for terminal execution events
  useEffect(() => {
    const handleTerminalStart = () => {
      isTerminalExecutingRef.current = true;
    };
    const handleTerminalEnd = () => {
      isTerminalExecutingRef.current = false;
      // to prevent triggering auto-add for loads created by terminal
      prevBeamCountRef.current =
        useBeamTrussStore.getState().beamTrusses.length;
      prevNodeCountRef.current = useTrussStore
        .getState()
        .getGroupedTruss().length;
    };

    window.addEventListener("terminal-execution-start", handleTerminalStart);
    window.addEventListener("terminal-execution-end", handleTerminalEnd);

    return () => {
      window.removeEventListener(
        "terminal-execution-start",
        handleTerminalStart
      );
      window.removeEventListener("terminal-execution-end", handleTerminalEnd);
    };
  }, []);

  // Loadcase Group Store
  const {
    loadcaseGroups,
    activeLoadcaseName,
    createLoadcaseGroup,
    deleteLoadcaseGroup,
    setActiveLoadcase,
    addBeamLoadToGroup,
    addNodeLoadToGroup,
    getBeamLoadIdsForLoadcase,
    getNodeLoadIdsForLoadcase,
  } = useLoadcaseGroupStore();

  // Sorted loadcases by creation time
  const sortedLoadcases = useMemo(
    () => [...loadcaseGroups].sort((a, b) => a.createdAt - b.createdAt),
    [loadcaseGroups]
  );

  const [expandedPanels, setExpandedPanels] = useState<Set<string>>(new Set());
  
  // Track which loadcase panel the user is currently working with (for adding loads)
  const [workingLoadcase, setWorkingLoadcase] = useState<string | null>(null);

  // Auto-generate next loadcase name (Loadcase1, Loadcase2, ...)
  const getNextLoadcaseName = useCallback(() => {
    let maxNum = 0;
    loadcaseGroups.forEach((g) => {
      const match = g.name.match(/^Loadcase(\d+)$/i);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > maxNum) maxNum = num;
      }
    });
    return `Loadcase${maxNum + 1}`;
  }, [loadcaseGroups]);

  useEffect(() => {
    useBeamTrussStore.getState().cleanCorruptedData();
  }, []);

  const [PanelVisible, setPanelVisible] = useState(false);

  const [dataMode, setDataMode] = useState<"beam" | "node">("beam");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedNodeType, setSelectedNodeType] = useState<
    "regular" | "perpendicular"
  >("regular");

  const [activationMessage, setActivationMessage] = useState<string | null>(
    null
  );
  const [isDirectMode, setIsDirectMode] = useState(false);
  const [manualPopupOpen, setManualPopupOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prevBeamCountRef = useRef(beamTrusses.length);
  const prevNodeCountRef = useRef(groupedTrussPresets.length);

  // Inline CSV editing state for B_ID column
  const [editingBeamIds, setEditingBeamIds] = useState<{
    loadIndex: number;
    value: string;
  } | null>(null);
  const [editingNodeIds, setEditingNodeIds] = useState<{
    presetIndex: number;
    value: string;
  } | null>(null);

  const { selectRow, unselectRow, activeRowData } =
    useTableSelection("loadcases");
  const { setCursorMode, clearCursorMode } = useCursorMode();
  const cancelAllApplyModes = useCancelAllApplyModes();

  const globalActiveTable = useGlobalSelectionStore(
    (state) => state.activeTable
  );
  const globalActiveRowId = useGlobalSelectionStore(
    (state) => state.activeRowId
  );

  // Support-style footer states
  // const [selectedLoad, setSelectedLoad] = useState<any>(null);

  const selectedLoad = activeRowData;
  const [applyByClickMode, setApplyByClickMode] = useState(false);
  const [applyToSelectedMode, setApplyToSelectedMode] = useState(false);

  const prevNodeIds = useRef<string[]>([]);
  const prevBeamIds = useRef<string[]>([]);

  const beamLoadTypes = [
    { value: "all", label: "All Types" },
    { value: "distributed_uniform", label: "Distributed 1" },
    { value: "distributed_variable", label: "Distributed 2" },
    { value: "concentrated_load", label: "Concentrated" },
    { value: "twisted_load", label: "Twisted" },
  ];

  // Handler to create a new loadcase group with auto-generated name
  const handleCreateLoadcaseGroup = useCallback(() => {
    const nextName = getNextLoadcaseName();
    try {
      const isFirstLoadcase = loadcaseGroups.length === 0;
      createLoadcaseGroup(nextName);
      
      // Only auto-activate if this is the first loadcase
      if (isFirstLoadcase) {
        setActiveLoadcase(nextName);
        setActivationMessage(`Created and activated "${nextName}"`);
      } else {
        setActivationMessage(`Created "${nextName}" (click to activate)`);
      }
      
      setTimeout(() => setActivationMessage(null), 2000);
    } catch (error: any) {
      setActivationMessage(error.message || "Failed to create loadcase");
      setTimeout(() => setActivationMessage(null), 3000);
    }
  }, [getNextLoadcaseName, createLoadcaseGroup, setActiveLoadcase, loadcaseGroups.length]);

  // Handler to delete a loadcase group
  const handleDeleteLoadcaseGroup = useCallback(
    (name: string) => {
      const confirmDelete = window.confirm(
        `Delete loadcase "${name}" and all its data?\n\nThis action cannot be undone.`
      );
      if (!confirmDelete) return;

      deleteLoadcaseGroup(name);
      setActivationMessage(`Deleted loadcase: "${name}"`);
      setTimeout(() => setActivationMessage(null), 2000);
    },
    [deleteLoadcaseGroup]
  );

  const getLoadDisplayName = (load: any, mode: "beam" | "node") => {
    return mode === "beam"
      ? (load?.label ?? "Unnamed Load")
      : (load?.name ?? "Unnamed Load");
  };

  // Helper function to click toolbar buttons
  const clickToolbarButton = useCallback((searchTexts: string[]) => {
    const buttons = document.querySelectorAll("button");
    for (const text of searchTexts) {
      const button = Array.from(buttons).find((btn) => {
        const btnText = btn.textContent?.trim();
        const isModelingButton = btn.closest('[class*="bg-gray-8"]') !== null;
        if (!isModelingButton) return false;
        return btnText === text || btnText?.includes(text);
      });
      if (button && button.getAttribute("aria-pressed") === "true") {
        button.click();
        return true;
      }
    }
    return false;
  }, []);

  useEffect(() => {
    // 🔹 BEAM LOAD ADDED - Auto-add to working loadcase group
    // Skip if terminal is executing (terminal handles its own group assignments)
    if (isTerminalExecutingRef.current) {
      prevBeamCountRef.current = beamTrusses.length;
      return;
    }

    if (beamTrusses.length > prevBeamCountRef.current) {
      const lastLoad = beamTrusses[beamTrusses.length - 1];

      setDataMode("beam");
      setSelectedType(lastLoad.type); // distributed_uniform, etc.

      // Auto-add to working loadcase group (the one user is interacting with)
      if (workingLoadcase) {
        addBeamLoadToGroup(workingLoadcase, lastLoad.id);
        setActivationMessage(
          `New Beam Load '${lastLoad.label}' added to "${workingLoadcase}"`
        );
      } else {
        setActivationMessage(
          `New Beam Load '${lastLoad.label}' added (no loadcase selected - expand a loadcase panel first)`
        );
      }
      setTimeout(() => setActivationMessage(null), 2000);
    }

    prevBeamCountRef.current = beamTrusses.length;
  }, [beamTrusses, workingLoadcase, addBeamLoadToGroup]);

  useEffect(() => {
    // 🔹 NODE LOAD ADDED - Auto-add to working loadcase group
    // Skip if terminal is executing (terminal handles its own group assignments)
    if (isTerminalExecutingRef.current) {
      prevNodeCountRef.current = groupedTrussPresets.length;
      return;
    }

    if (groupedTrussPresets.length > prevNodeCountRef.current) {
      const lastPreset = groupedTrussPresets[groupedTrussPresets.length - 1];

      setDataMode("node");
      setSelectedNodeType(lastPreset.type); // regular / perpendicular

      // Auto-add to working loadcase group (the one user is interacting with)
      if (workingLoadcase) {
        addNodeLoadToGroup(workingLoadcase, lastPreset.id);
        setActivationMessage(
          `New Node Load '${lastPreset.name}' added to "${workingLoadcase}"`
        );
      } else {
        setActivationMessage(
          `New Node Load '${lastPreset.name}' added (no loadcase selected - expand a loadcase panel first)`
        );
      }
      setTimeout(() => setActivationMessage(null), 2000);
    }

    prevNodeCountRef.current = groupedTrussPresets.length;
  }, [groupedTrussPresets, workingLoadcase, addNodeLoadToGroup]);

  // Helper function to disable all modeling modes
  const disableAllModes = useCallback(() => {
    const modelStore = useModelStore.getState();

    // Click toolbar buttons to toggle them off
    clickToolbarButton(["Create Node", "Node"]);
    clickToolbarButton(["Create Beam", "Beam"]);
    clickToolbarButton(["Prep Mode"]);

    // Cancel states in callbacks
    if (callbacks?.setIsNodeCreationMode) {
      callbacks.setIsNodeCreationMode(false);
    }

    if (callbacks?.setIsBeamCreationMode) {
      callbacks.setIsBeamCreationMode(false);
    }

    // Cancel prep mode
    if (modelStore.isPrepMode) {
      modelStore.cancelPrepMode();
    }

    // Cancel divide beam mode
    if (modelStore.isDivideBeamMode) modelStore.cancelDivideBeamMode();

    // Cancel beam creation
    if (modelStore.cancelBeamCreation) {
      modelStore.cancelBeamCreation();
    }

    // Reset cursor
    clearCursorMode();
  }, [callbacks, clearCursorMode, clickToolbarButton]);

  const handleDeleteLoad = (load: any) => {
    if (!load) return;

    const beamCount = load.beamIds?.length || 0;
    const confirmMsg =
      beamCount > 0
        ? `Delete '${load.name}' from ${beamCount} beam(s) and remove all assignments?`
        : `Delete '${load.name}' and remove it from all assignments?`;

    const confirmDelete = window.confirm(confirmMsg);
    if (!confirmDelete) return;

    const { removeBeamTruss } = useBeamTrussStore.getState();
    removeBeamTruss(load.id);

    unselectRow();
    setApplyByClickMode(false);
    setApplyToSelectedMode(false);

    setActivationMessage(`Deleted '${load.name}' and removed all assignments.`);
    setTimeout(() => setActivationMessage(""), 1000);
  };

  const nodePresets = useMemo<TrussPreset[]>(() => {
    return groupedTrussPresets.map((g: any) => ({
      id: g.id,
      name: g.presetName || g.name,
      type: g.type,
      nodeIds: Array.isArray(g.nodeIds) ? g.nodeIds.flat() : [],
      rotationValues: {
        X: g.rotationValues?.X ?? 0,
        Y: g.rotationValues?.Y ?? 0,
        Z: g.rotationValues?.Z ?? 0,
      },
      perpendicularValues: {
        X: g.perpendicularValues?.X ?? 0,
        Y: g.perpendicularValues?.Y ?? 0,
        Z: g.perpendicularValues?.Z ?? 0,
      },
    }));
  }, [groupedTrussPresets]);

  const handleDeleteNodePreset = (preset: TrussPreset | any) => {
    if (!preset) return;

    const nodeCount = preset.nodeIds?.length || 0;
    const confirmMsg =
      nodeCount > 0
        ? `Delete '${preset.name}' from ${nodeCount} node(s) and remove all assignments?`
        : `Delete '${preset.name}' and remove it from all assigned nodes?`;

    const confirmDelete = window.confirm(confirmMsg);
    if (!confirmDelete) return;

    // Remove from truss store
    const { nodeTrusses, removeTruss } = useTrussStore.getState();

    nodeTrusses.forEach((t) => {
      if (t.presetName?.toLowerCase() === preset.name.toLowerCase()) {
        removeTruss(t.id);
      }
    });

    unselectRow();
    setApplyByClickMode(false);
    setApplyToSelectedMode(false);

    setActivationMessage(
      `Deleted '${preset.name}' and removed all its trusses.`
    );
    setTimeout(() => setActivationMessage(null), 2000);
  };

  const filteredBeamLoads = beamTrusses.filter(
    (t) => selectedType === "all" || t.type === selectedType
  );

  const filteredNodeLoads = nodePresets.filter(
    (preset) => preset.type === selectedNodeType
  );

  // Group beam loads by identical properties (type, category, and all values)
  // Include label in key so each named load appears separately
  const groupedBeamLoads = useMemo(() => {
    const map = new Map<string, any>();

    filteredBeamLoads.forEach((load) => {
      // Include label to keep unique loads separate
      const key = JSON.stringify({
        label: load.label, // Include label to keep unique loads separate
        type: load.type,
        dx: load.dx,
        dy: load.dy,
        dz: load.dz,
        dxLeft: load.dxLeft,
        dyLeft: load.dyLeft,
        dzLeft: load.dzLeft,
        dxRight: load.dxRight,
        dyRight: load.dyRight,
        dzRight: load.dzRight,
        fx: load.fx,
        fy: load.fy,
        fz: load.fz,
        torque: load.torque,
        torqueDirection: load.torqueDirection,
      });

      if (!map.has(key)) {
        map.set(key, { ...load, loadIds: [load.id] });
      } else {
        map.get(key).loadIds.push(load.id);
      }
    });

    return Array.from(map.values());
  }, [filteredBeamLoads]);

  // Start editing beam IDs (must be after groupedBeamLoads)
  const startEditingBeams = useCallback(
    (e: React.MouseEvent, loadIndex: number, currentBeamIds: string[]) => {
      e.stopPropagation();
      setEditingBeamIds({
        loadIndex,
        value: currentBeamIds.join(", "),
      });
    },
    []
  );

  const applyByClick = useCallback(
    (load: any, id: string) => {
      if (!load || !id) return;

      if (dataMode === "beam") {
        const store = useBeamTrussStore.getState();
        const freshLoad = store.beamTrusses.find((t) => t.id === load.id);

        if (!freshLoad) return;

        const success = store.applyLoadToBeams(freshLoad.id, [id]);

        if (success) {
          setActivationMessage(`Applied ${freshLoad.label} to Beam ${id}`);
          setTimeout(() => setActivationMessage(null), 1500);

          setTimeout(() => {
            window.dispatchEvent(new CustomEvent("loads-updated"));
          }, 50);
        }
      } else {
        // Apply new load (allows multiple different loads from different loadcases on same node)
        const store = useTrussStore.getState();
        store.setSelectedPresetName(load.name);
        store.applyLoadOnNode(
          [id],
          load.rotationValues,
          load.perpendicularValues
        );
        store.setSelectedPresetName(null);

        setActivationMessage(`✅ Applied "${load.name}" to Node ${id}`);
        setTimeout(() => setActivationMessage(null), 1500);

        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("loads-updated"));
          window.dispatchEvent(
            new CustomEvent("predefined_truss_presets_updated")
          );
        }, 50);
      }
    },
    [dataMode, setActivationMessage]
  );

  // ✅ CLICK-ONLY: Track previous selection
  useEffect(() => {
    if (!applyByClickMode || !selectedLoad) return;

    const handleApplyByClickNode = (event: CustomEvent) => {
      const { nodeId } = event.detail;

      // Only process if loadcases table is active
      if (globalActiveTable !== "loadcases") {
        return;
      }

      if (dataMode === "node") {
        applyByClick(selectedLoad, nodeId);

        // Clear selection immediately after applying
        setTimeout(() => {
          const modelStore = useModelStore.getState();
          modelStore.clearSelection();
        }, 0);
      }
    };

    window.addEventListener(
      "applyByClickNode",
      handleApplyByClickNode as EventListener
    );

    return () => {
      window.removeEventListener(
        "applyByClickNode",
        handleApplyByClickNode as EventListener
      );
    };
  }, [
    applyByClickMode,
    selectedLoad,
    dataMode,
    applyByClick,
    globalActiveTable,
  ]);

  // Event-based apply-by-click for beams - WITH ACTIVE TABLE CHECK
  useEffect(() => {
    if (!applyByClickMode || !selectedLoad) return;

    const handleApplyByClickBeam = (event: CustomEvent) => {
      const { beamId } = event.detail;

      // Only process if loadcases table is active
      if (globalActiveTable !== "loadcases") {
        return;
      }

      if (dataMode === "beam") {
        applyByClick(selectedLoad, beamId);

        // Clear selection immediately after applying
        setTimeout(() => {
          const modelStore = useModelStore.getState();
          modelStore.clearSelection();
        }, 0);
      }
    };

    window.addEventListener(
      "applyByClickBeam",
      handleApplyByClickBeam as EventListener
    );

    return () => {
      window.removeEventListener(
        "applyByClickBeam",
        handleApplyByClickBeam as EventListener
      );
    };
  }, [
    applyByClickMode,
    selectedLoad,
    dataMode,
    applyByClick,
    globalActiveTable,
  ]);

  // Reset refs when mode changes
  useEffect(() => {
    if (!applyByClickMode) {
      prevNodeIds.current = [];
      prevBeamIds.current = [];
    }
  }, [applyByClickMode]);

  const applyToSelected = useCallback(() => {
    if (!selectedLoad) return;

    const modelStore = useModelStore.getState();
    if (dataMode === "beam") {
      const selectedBeamIds = modelStore.selectedBeamIds;
      if (selectedBeamIds.length === 0) return;

      useBeamTrussStore
        .getState()
        .applyLoadToBeams(selectedLoad.id, selectedBeamIds);

      setApplyToSelectedMode(false);
      unselectRow();
      clearCursorMode();
      setActivationMessage(
        `Applied "${selectedLoad.label || selectedLoad.id}" to ${selectedBeamIds.length} beams`
      );
      setTimeout(() => setActivationMessage(null), 2000);

      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("loads-updated"));
      }, 50);
    } else {
      // Node mode

      const selectedNodeIds = modelStore.selectedNodeIds;
      if (selectedNodeIds.length === 0) return;

      // ✅ DEFINE trussStore HERE
      const trussStore = useTrussStore.getState();

      // Apply loads to all selected nodes (allows multiple different loadcases on same nodes)
      trussStore.setSelectedPresetName(selectedLoad.name);
      trussStore.applyLoadOnNode(
        selectedNodeIds,
        selectedLoad.rotationValues,
        selectedLoad.perpendicularValues
      );
      trussStore.setSelectedPresetName(null);

      setActivationMessage(`✅ Applied "${selectedLoad.name}" to ${selectedNodeIds.length} node(s)`);
      setTimeout(() => setActivationMessage(null), 2000);
    }

    setApplyToSelectedMode(false);
    unselectRow();
    clearCursorMode();

    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("loads-updated"));
      window.dispatchEvent(new CustomEvent("predefined_truss_presets_updated"));
    }, 50);
  }, [selectedLoad, dataMode]);

  const applyManual = useCallback(
    (load: any, ids: string[] | string) => {
      if (!load || ids === null || ids === undefined) return;

      if (dataMode === "beam") {
        // BEAM MODE - Add/Remove Logic
        const beamTrussStore = useBeamTrussStore.getState();
        const modelStore = useModelStore.getState();

        // Parse input IDs
        const newBeamIds: string[] = Array.isArray(ids)
          ? ids
          : (ids as string)
              .split(",")
              .map((id: string) => id.trim())
              .filter(Boolean);

        // Get current beam IDs
        const currentBeamIds: string[] = Array.isArray(load.beamIds)
          ? load.beamIds
          : [];

        // Calculate what to ADD and what to REMOVE
        const beamsToAdd = newBeamIds.filter(
          (id: string) => !currentBeamIds.includes(id)
        );
        const beamsToRemove = currentBeamIds.filter(
          (id: string) => !newBeamIds.includes(id)
        );

        if (beamsToAdd.length > 0) {
          // Just check if beams exist
          const validBeamIds = new Set(modelStore.beams.map((b) => b.id));
          const missingBeams = beamsToAdd.filter((id) => !validBeamIds.has(id));

          if (missingBeams.length > 0) {
            alert(
              `Cannot assign loadcase to ${missingBeams.length} beam(s):\n\n` +
                `${missingBeams.join(", ")}\n\n` +
                `These beams do not exist in the model.\n` +
                `Please create them first, then try again.`
            );
            return;
          }
        }

        // Find the load in the store and update its beamIds
        const loadIndex = beamTrussStore.beamTrusses.findIndex(
          (bt: any) => bt.id === load.id || bt.label === load.label
        );

        if (loadIndex !== -1) {
          useBeamTrussStore.setState((state) => ({
            beamTrusses: state.beamTrusses.map((bt, idx) =>
              idx === loadIndex ? { ...bt, beamIds: newBeamIds } : bt
            ),
          }));
        }

        // User Feedback
        const addedCount = beamsToAdd.length;
        const removedCount = beamsToRemove.length;

        const message =
          addedCount > 0 && removedCount > 0
            ? `✅ Updated Load: Added ${addedCount}, Removed ${removedCount} beam(s)`
            : addedCount > 0
              ? `✅ Added ${addedCount} beam(s) to ${load.label || load.name}`
              : removedCount > 0
                ? `✅ Removed ${removedCount} beam(s) from ${load.label || load.name}`
                : `✅ Load unchanged (${newBeamIds.length} beam(s))`;

        setActivationMessage(message);
        setTimeout(() => setActivationMessage(null), 3000);

        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("loads-updated"));
        }, 50);
      } else {
        // NODE MODE - Add/Remove Logic
        const trussStore = useTrussStore.getState();

        const modelStore = useModelStore.getState();

        // Parse input IDs
        const newNodeIds: string[] = Array.isArray(ids)
          ? ids
          : (ids as string)
              .split(",")
              .map((id: string) => id.trim())
              .filter(Boolean);

        // Get current node IDs
        const currentNodeIds: string[] = Array.isArray(load.nodeIds)
          ? load.nodeIds
          : [];

        // Calculate what to ADD and what to REMOVE
        const nodesToAdd = newNodeIds.filter(
          (id: string) => !currentNodeIds.includes(id)
        );
        const nodesToRemove = currentNodeIds.filter(
          (id: string) => !newNodeIds.includes(id)
        );

        if (nodesToAdd.length > 0) {
          const missingNodes = nodesToAdd.filter(
            (nodeId: string) =>
              !modelStore.nodes.find((n) => String(n.id) === String(nodeId))
          );

          if (missingNodes.length > 0) {
            alert(
              `Cannot assign loadcase to ${missingNodes.length} node(s):\n\n` +
                `${missingNodes.join(", ")}\n\n` +
                `These nodes do not exist in the model.\n` +
                `Please create them first, then try again.`
            );
            return;
          }
        }

        // Note: Nodes CAN have loads from different loadcases (only one loadcase is active at a time)
        // Only prevent duplicate assignment of the SAME load to a node

        // REMOVE: Remove load from nodes that are no longer in the list
        if (nodesToRemove.length > 0) {
          nodesToRemove.forEach((nodeId: string) => {
            // Find ALL trusses that have this preset name and contain this nodeId
            const matchingTrusses = trussStore.nodeTrusses.filter(
              (t: any) =>
                t.presetName?.toLowerCase() === load.name.toLowerCase() &&
                t.nodeIds?.includes(nodeId)
            );

            matchingTrusses.forEach((existingTruss: any) => {
              const updatedNodeIds = existingTruss.nodeIds.filter(
                (id: string) => id !== nodeId
              );

              if (updatedNodeIds.length === 0) {
                // If no nodes left, remove the entire truss
                trussStore.removeTruss(existingTruss.id);
              } else {
                // Try to update if method exists, otherwise recreate
                if (typeof trussStore.updateTruss === "function") {
                  trussStore.updateTruss(existingTruss.id, {
                    ...existingTruss,
                    nodeIds: updatedNodeIds,
                  });
                } else {
                  // Fallback: Remove old and create new with updated nodeIds
                  trussStore.removeTruss(existingTruss.id);
                  trussStore.setSelectedPresetName(load.name);
                  trussStore.applyLoadOnNode(
                    updatedNodeIds,
                    load.rotationValues,
                    load.perpendicularValues
                  );
                  trussStore.setSelectedPresetName(null);
                }
              }
            });
          });
        }

        // Apply load to new nodes
        if (nodesToAdd.length > 0) {
          trussStore.setSelectedPresetName(load.name);
          trussStore.applyLoadOnNode(
            nodesToAdd,
            load.rotationValues,
            load.perpendicularValues
          );
          trussStore.setSelectedPresetName(null);
        }

        // User Feedback
        const addedCount = nodesToAdd.length;
        const removedCount = nodesToRemove.length;

        const message =
          addedCount > 0 && removedCount > 0
            ? `✅ Updated Load: Added ${addedCount}, Removed ${removedCount} node(s)`
            : addedCount > 0
              ? `✅ Added ${addedCount} node(s) to ${load.name}`
              : removedCount > 0
                ? `✅ Removed ${removedCount} node(s) from ${load.name}`
                : `✅ Load unchanged (${newNodeIds.length} node(s))`;

        setActivationMessage(message);
        setTimeout(() => setActivationMessage(null), 3000);
      }

      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("loads-updated"));
        window.dispatchEvent(
          new CustomEvent("predefined_truss_presets_updated")
        );
      }, 50);

      setManualPopupOpen(false);
      unselectRow();
      clearCursorMode();
    },
    [
      dataMode,
      unselectRow,
      clearCursorMode,
      setManualPopupOpen,
      setActivationMessage,
    ]
  );

  // Commit edited beam IDs (must be after applyManual)
  const commitBeamIdsEdit = useCallback(() => {
    if (!editingBeamIds) return;

    const load = groupedBeamLoads[editingBeamIds.loadIndex];
    if (!load) {
      setEditingBeamIds(null);
      return;
    }

    const input = editingBeamIds.value.trim();

    // Parse CSV - split by comma, trim whitespace, filter empty
    const beamIdsArray = Array.from(
      new Set(
        input
          .split(",")
          .map((id) => id.trim())
          .filter((id) => id !== "")
      )
    );

    // Allow empty (removes all beams from this load)
    if (beamIdsArray.length === 0) {
      applyManual(load, []); // Pass empty array to clear all assignments
      setEditingBeamIds(null);
      return;
    }

    // Validation: Check all are numeric
    const nonNumeric = beamIdsArray.filter((id) => !/^\d+$/.test(id));
    if (nonNumeric.length > 0) {
      alert(
        `Invalid beam IDs (must be numeric only):\n${nonNumeric.join(", ")}`
      );
      setEditingBeamIds(null);
      return;
    }

    // Validation: Check if beams exist
    const modelStore = useModelStore.getState();
    const validBeamIds = new Set(modelStore.beams.map((b) => b.id));
    const missingBeams = beamIdsArray.filter((id) => !validBeamIds.has(id));

    if (missingBeams.length > 0) {
      alert(
        `The following beam IDs do not exist in the beam table:\n${missingBeams.join(", ")}`
      );
      setEditingBeamIds(null);
      return;
    }

    // Apply changes using applyManual which handles add/remove logic
    applyManual(load, beamIdsArray);
    setEditingBeamIds(null);
  }, [editingBeamIds, groupedBeamLoads, applyManual]);

  // Start editing node IDs (ADD THIS)
  const startEditingNodes = useCallback(
    (e: React.MouseEvent, presetIndex: number, currentNodeIds: string[]) => {
      e.stopPropagation();
      setEditingNodeIds({
        presetIndex,
        value: currentNodeIds.join(", "),
      });
    },
    []
  );

  // Commit edited node IDs (ADD THIS) - WITH DUPLICATE FIX
  const commitNodeIdsEdit = useCallback(() => {
    if (!editingNodeIds) return;

    const preset = filteredNodeLoads[editingNodeIds.presetIndex];
    if (!preset) {
      setEditingNodeIds(null);
      return;
    }

    const input = editingNodeIds.value.trim();

    // Remove duplicates while preserving order
    const nodeIdsArray = Array.from(
      new Set(
        input
          .split(",")
          .map((id) => id.trim())
          .filter((id) => id !== "")
      )
    );

    if (nodeIdsArray.length === 0) {
      applyManual(preset, []); // Pass empty array to clear all assignments
      setEditingNodeIds(null);
      return;
    }

    // Validation: Check all are numeric
    const nonNumeric = nodeIdsArray.filter((id) => !/^\d+$/.test(id));
    if (nonNumeric.length > 0) {
      alert(
        `Invalid node IDs (must be numeric only):\n${nonNumeric.join(", ")}`
      );
      setEditingNodeIds(null);
      return;
    }

    // Validation: Check if nodes exist
    const modelStore = useModelStore.getState();
    const validNodeIds = new Set(modelStore.nodes.map((n: any) => n.id));
    const missingNodes = nodeIdsArray.filter((id) => !validNodeIds.has(id));
    if (missingNodes.length > 0) {
      alert(`The following node IDs do not exist:\n${missingNodes.join(", ")}`);
      setEditingNodeIds(null);
      return;
    }

    // Apply changes using applyManual
    applyManual(preset, nodeIdsArray);
    setEditingNodeIds(null);
  }, [editingNodeIds, filteredNodeLoads, applyManual]);

  // Import/Export/Copy handlers

  const handleExport = useCallback(() => {
    try {
      if (dataMode === "beam") {
        if (beamTrusses.length === 0) {
          alert("No beam loads to export! Please add beam loads first.");
          return;
        }
        exportBeamLoadsToCSV(beamTrusses);
        setActivationMessage("✅ Beam loads exported successfully");
        setTimeout(() => setActivationMessage(null), 3000);
      } else {
        if (nodePresets.length === 0) {
          alert("No node loads to export! Please add node loads first.");
          return;
        }
        exportNodeLoadsToCSV(nodePresets);
        setActivationMessage("✅ Node loads exported successfully");
        setTimeout(() => setActivationMessage(null), 3000);
      }
    } catch (error) {
      console.error("❌ Export failed:", error);
      setActivationMessage(
        "❌ Export failed: " +
          (error instanceof Error ? error.message : String(error))
      );
      setTimeout(() => setActivationMessage(null), 5000);
    }
  }, [dataMode, beamTrusses, nodePresets, setActivationMessage]);

  const handleCopy = useCallback(async () => {
    try {
      if (dataMode === "beam") {
        if (beamTrusses.length === 0) {
          alert("No beam loads to copy!");
          return;
        }
        const success = await copyBeamLoadsToClipboard(beamTrusses);
        if (success) {
          setActivationMessage(`✅ Beam loads copied to clipboard!`);
          setTimeout(() => setActivationMessage(null), 2000);
        } else {
          setActivationMessage("❌ Copy failed - Use Export button instead");
          setTimeout(() => setActivationMessage(null), 2000);
        }
      } else {
        if (nodePresets.length === 0) {
          alert("No node loads to copy!");
          return;
        }
        const success = await copyNodeLoadsToClipboard(nodePresets);
        if (success) {
          setActivationMessage(`✅ Node loads copied to clipboard!`);
          setTimeout(() => setActivationMessage(null), 2000);
        } else {
          setActivationMessage("❌ Copy failed - Use Export button instead");
          setTimeout(() => setActivationMessage(null), 2000);
        }
      }
    } catch (error) {
      console.error("Copy failed:", error);
      alert("❌ Copy failed - Use Export button instead");
    }
  }, [dataMode, beamTrusses, nodePresets]);

  const handleFileSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      try {
        let result: any;
        let detectedType: "beam" | "node" | "unknown" = "unknown";

        if (fileExtension === "csv") {
          // First pass: Detect type from headers
          await new Promise<void>((resolve) => {
            Papa.parse(file, {
              preview: 1,
              header: true,
              complete: (results: any) => {
                const headers = results.meta.fields || [];
                detectedType = detectLoadTypeFromHeaders(headers as string[]);
                resolve();
              },
              error: () => {
                detectedType = "unknown";
                resolve();
              },
            });
          });

          // Show unknown type warning with better dialog
          if (detectedType === "unknown") {
            const userChoice = window.confirm(
              `Cannot auto-detect load type from CSV headers.
                Expected headers for detection:
                • Beam Loads: Load_Name, Beam_ID, Type, dx, dy, dz...
                • Node Loads: Load_Name, Node_ID, rotationX, rotationY, rotationZ...

                Click OK for Beam Loads, Cancel for Node Loads.`
            );
            detectedType = userChoice ? "beam" : "node";
          }
        } else if (fileExtension === "xlsx" || fileExtension === "xls") {
          // Handle Excel files
          setActivationMessage("📊 Processing Excel file...");
          const excelResult = await processExcelFile(file);
          if (excelResult.success && excelResult.data.length > 0) {
            const headers = Object.keys(excelResult.data[0]);
            detectedType = detectLoadTypeFromHeaders(headers);
            result = { success: true, data: excelResult.data };
          } else {
            throw new Error(
              excelResult.errors?.join(", ") || "Failed to process Excel file"
            );
          }

          if (detectedType === "unknown") {
            const userChoice = window.confirm(
              `Cannot auto-detect load type from Excel headers.\n\nClick OK for Beam Loads, Cancel for Node Loads.`
            );
            detectedType = userChoice ? "beam" : "node";
          }
        } else {
          setActivationMessage(
            "❌ Unsupported file type. Please use CSV or Excel (.xlsx/.xls) files."
          );
          setTimeout(() => setActivationMessage(null), 4000);
          if (fileInputRef.current) fileInputRef.current.value = "";
          return;
        }

        // BEAM LOADS IMPORT
        if (detectedType === "beam") {
          if (fileExtension === "csv") {
            result = await importBeamLoadsFromCSV(file);
          }
          // For Excel, process the already loaded data
          if (!result?.success && fileExtension !== "csv") {
            // Process Excel data into beam loads format
            const loadMap = new Map<string, any>();
            result.data.forEach((row: any, index: number) => {
              const loadName =
                row.LoadName ||
                row.Load_Name ||
                row.loadName ||
                `Load_${index + 1}`;
              const beamId = row.BeamID || row.Beam_ID || row.beamId;
              const type = row.Type || row.type || "distributed_uniform";

              if (!loadMap.has(loadName)) {
                loadMap.set(loadName, {
                  id: Date.now().toString() + Math.random(),
                  label: loadName,
                  type,
                  beamIds: [],
                  dx: parseFloat(row.dx) || 0,
                  dy: parseFloat(row.dy) || 0,
                  dz: parseFloat(row.dz) || 0,
                  dxLeft: parseFloat(row.dxStart || row.dxLeft) || 0,
                  dyLeft: parseFloat(row.dyStart || row.dyLeft) || 0,
                  dzLeft: parseFloat(row.dzStart || row.dzLeft) || 0,
                  dxRight: parseFloat(row.dxEnd || row.dxRight) || 0,
                  dyRight: parseFloat(row.dyEnd || row.dyRight) || 0,
                  dzRight: parseFloat(row.dzEnd || row.dzRight) || 0,
                  fx: parseFloat(row.fx) || 0,
                  fy: parseFloat(row.fy) || 0,
                  fz: parseFloat(row.fz) || 0,
                  referenceNode: row.referenceNode || "S_Node",
                  percentageFromNode: parseFloat(row.percentageFromNode) || 0,
                  torque: parseFloat(row.torque) || 0,
                  torqueDirection: row.torqueDirection || "clockwise",
                });
              }
              const loadData = loadMap.get(loadName);
              if (beamId) loadData.beamIds.push(beamId.toString());
            });
            result = { success: true, data: Array.from(loadMap.values()) };
          }

          if (result.success) {
            const totalLoads = result.data.length;
            const totalBeamAssignments = result.data.reduce(
              (sum: number, load: any) => sum + (load.beamIds?.length || 0),
              0
            );

            const loadsByType: Record<string, number> = {};
            result.data.forEach((load: any) => {
              const type = load.type || "unknown";
              loadsByType[type] = (loadsByType[type] || 0) + 1;
            });
            const typeBreakdown = Object.entries(loadsByType)
              .map(([type, count]) => `${type}: ${count}`)
              .join(", ");

            const confirmMsg = `Import ${totalLoads} beam load${totalLoads !== 1 ? "s" : ""}?\n\nLoad types: ${typeBreakdown}\nBeam assignments: ${totalBeamAssignments}\n\nThis will ADD to existing loads.\n\nClick OK to proceed, Cancel to abort.`;

            if (!window.confirm(confirmMsg)) {
              setActivationMessage("Import cancelled by user");
              setTimeout(() => setActivationMessage(null), 2000);
              if (fileInputRef.current) fileInputRef.current.value = "";
              return;
            }

            const beamTrussStore = useBeamTrussStore.getState();
            const loadcaseGroupStore = useLoadcaseGroupStore.getState();
            let successCount = 0;
            let errorCount = 0;

            let targetLoadcase = activeLoadcaseName || "";
            if (!targetLoadcase) {
              const newLoadcaseName = getNextLoadcaseName();
              try {
                loadcaseGroupStore.createLoadcaseGroup(newLoadcaseName);
                targetLoadcase = newLoadcaseName;
              } catch (error) {
                // If loadcase already exists, try to use it or generate another name
                console.warn(
                  `Loadcase "${newLoadcaseName}" already exists, trying to use it or create a unique one`
                );
                const existing = loadcaseGroups.find(
                  (g) => g.name === newLoadcaseName
                );
                if (existing) {
                  targetLoadcase = newLoadcaseName;
                } else {
                  // Generate a unique name with timestamp
                  const uniqueName = `Loadcase_${Date.now()}`;
                  loadcaseGroupStore.createLoadcaseGroup(uniqueName);
                  targetLoadcase = uniqueName;
                }
              }
            }

            isTerminalExecutingRef.current = true;

            result.data.forEach((load: any) => {
              try {
                const newId = beamTrussStore.addBeamTruss(
                  load.beamIds || [],
                  load.type,
                  0,
                  load.dxLeft || load.dx || 0,
                  load.dyLeft || load.dy || 0,
                  load.dzLeft || load.dz || 0,
                  load.dxRight,
                  load.dyRight,
                  load.dzRight,
                  load.fx,
                  load.fy,
                  load.fz,
                  load.referenceNode,
                  load.percentageFromNode,
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  load.label,
                  load.torque,
                  load.torqueDirection
                );

                if (newId) {
                  loadcaseGroupStore.addBeamLoadToGroup(targetLoadcase, newId);
                }
                successCount++;
              } catch (error) {
                console.error(`Failed to import load "${load.label}":`, error);
                errorCount++;
              }
            });

            isTerminalExecutingRef.current = false;
            prevBeamCountRef.current = beamTrussStore.beamTrusses.length;

            const message =
              errorCount > 0
                ? `⚠️ Imported ${successCount}/${totalLoads} beam loads to "${targetLoadcase}" (${errorCount} failed)`
                : `✅ Imported ${successCount} beam loads to "${targetLoadcase}" (${typeBreakdown})`;

            setActivationMessage(message);
            setTimeout(() => setActivationMessage(null), 5000);
            setDataMode("beam");
            setSelectedType("all");

            if (targetLoadcase !== activeLoadcaseName) {
              loadcaseGroupStore.setActiveLoadcase(targetLoadcase);
            }
          } else {
            throw new Error(
              result.errors?.join(", ") || "Failed to parse beam loads"
            );
          }
        }

        // NODE LOADS IMPORT
        else if (detectedType === "node") {
          if (fileExtension === "csv") {
            result = await importNodeLoadsFromCSV(file);
            if (!result.success) {
              throw new Error(
                result.errors?.join(", ") ||
                  "Failed to parse node loads from CSV"
              );
            }
          } else if (fileExtension === "xlsx" || fileExtension === "xls") {
            // Process Excel data that was already loaded earlier
            const presetMap = new Map<string, any>();
            result.data.forEach((row: any, index: number) => {
              const loadName =
                row.LoadName ||
                row.Load_Name ||
                row.loadName ||
                `NodeLoad_${index + 1}`;
              const nodeId = row.NodeID || row.Node_ID || row.nodeId;
              const type = (row.type || row.Type || "regular").toLowerCase();

              if (!presetMap.has(loadName)) {
                presetMap.set(loadName, {
                  id: row.Load_ID || Date.now().toString() + Math.random(),
                  name: loadName,
                  type: type === "perpendicular" ? "perpendicular" : "regular",
                  nodeIds: [],
                  rotationValues: {
                    X: parseFloat(row.rotationX) || 0,
                    Y: parseFloat(row.rotationY) || 0,
                    Z: parseFloat(row.rotationZ) || 0,
                  },
                  perpendicularValues: {
                    X: parseFloat(row.perpendicularX) || 0,
                    Y: parseFloat(row.perpendicularY) || 0,
                    Z: parseFloat(row.perpendicularZ) || 0,
                  },
                });
              }
              const preset = presetMap.get(loadName);
              if (nodeId) preset.nodeIds.push(nodeId.toString());
            });
            result = { success: true, data: Array.from(presetMap.values()) };
          }

          if (result.success) {
            const totalPresets = result.data.length;
            const totalNodeAssignments = result.data.reduce(
              (sum: number, preset: any) => sum + (preset.nodeIds?.length || 0),
              0
            );

            const confirmMsg = `Import ${totalPresets} node load${totalPresets !== 1 ? "s" : ""} with ${totalNodeAssignments} node assignment${totalNodeAssignments !== 1 ? "s" : ""}?\n\nThis will ADD to existing node loads.\n\nClick OK to proceed, Cancel to abort.`;

            if (!window.confirm(confirmMsg)) {
              setActivationMessage("Import cancelled by user");
              setTimeout(() => setActivationMessage(null), 2000);
              if (fileInputRef.current) fileInputRef.current.value = "";
              return;
            }

            const loadcaseGroupStore = useLoadcaseGroupStore.getState();

            let targetLoadcase = activeLoadcaseName || "";
            if (!targetLoadcase) {
              const newLoadcaseName = getNextLoadcaseName();
              try {
                loadcaseGroupStore.createLoadcaseGroup(newLoadcaseName);
                targetLoadcase = newLoadcaseName;
              } catch (error) {
                // If loadcase already exists, try to use it or generate another name
                console.warn(
                  `Loadcase "${newLoadcaseName}" already exists, trying to use it or create a unique one`
                );
                const existing = loadcaseGroups.find(
                  (g) => g.name === newLoadcaseName
                );
                if (existing) {
                  targetLoadcase = newLoadcaseName;
                } else {
                  // Generate a unique name with timestamp
                  const uniqueName = `Loadcase_${Date.now()}`;
                  loadcaseGroupStore.createLoadcaseGroup(uniqueName);
                  targetLoadcase = uniqueName;
                }
              }
            }

            isTerminalExecutingRef.current = true;

            // Apply ALL loads to truss store (even without node assignments)
            const trussStore = useTrussStore.getState();
            let appliedCount = 0;
            let presetsWithoutNodes = 0;

            result.data.forEach((preset: any) => {
              const nodeIds = Array.isArray(preset.nodeIds)
                ? preset.nodeIds
                : [];

              // Set preset name for truss store
              trussStore.setSelectedPresetName(preset.name);

              // Create load in truss store (even with empty nodeIds)
              trussStore.applyLoadOnNode(
                nodeIds, // Can be empty array
                preset.rotationValues,
                preset.perpendicularValues
              );

              if (nodeIds.length === 0) {
                presetsWithoutNodes++;
              } else {
                appliedCount += nodeIds.length;
              }

              trussStore.setSelectedPresetName(null);

              // Add to loadcase group if any grouped trusses were created
              const groupedTrusses = trussStore.getGroupedTruss();
              const groupedPreset = groupedTrusses.find(
                (g: any) => g.name === preset.name
              );
              if (groupedPreset) {
                loadcaseGroupStore.addNodeLoadToGroup(
                  targetLoadcase,
                  groupedPreset.id
                );
              }
            });

            isTerminalExecutingRef.current = false;
            prevNodeCountRef.current = trussStore.getGroupedTruss().length;

            // Build user-friendly message
            const message =
              presetsWithoutNodes > 0
                ? appliedCount > 0
                  ? `✅ Imported ${totalPresets} node load(s): ${appliedCount} applied, ${presetsWithoutNodes} saved without nodes (assign manually in table)`
                  : `✅ Imported ${totalPresets} node load definition(s). Use "Node(s)" column or Apply buttons to assign nodes.`
                : `✅ Imported ${totalPresets} node loads to "${targetLoadcase}" (${appliedCount} node assignments applied)`;

            setActivationMessage(message);
            setTimeout(() => setActivationMessage(null), 6000);

            setDataMode("node");

            if (targetLoadcase !== activeLoadcaseName) {
              loadcaseGroupStore.setActiveLoadcase(targetLoadcase);
            }
          } else {
            throw new Error(
              result.errors?.join(", ") || "Failed to parse node loads"
            );
          }
        }

        if (fileInputRef.current) fileInputRef.current.value = "";
      } catch (error) {
        console.error("Import error:", error);
        setActivationMessage(
          `❌ Import failed: ${error instanceof Error ? error.message : String(error)}`
        );
        setTimeout(() => setActivationMessage(null), 6000);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [dataMode, beamTrusses, nodePresets, setActivationMessage, setDataMode]
  );

  /** CLICK MODE — APPLY LOAD IMMEDIATELY WHEN SELECTION CHANGES */

  const cancelApplyByClick = useCallback(() => {
    setApplyByClickMode(false);
    clearCursorMode();
    unselectRow();
    setActivationMessage("Apply by Click mode canceled");
    setTimeout(() => setActivationMessage(null), 2000);
  }, [clearCursorMode, unselectRow]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && applyByClickMode) {
        e.preventDefault();
        e.stopPropagation();
        cancelApplyByClick();
      }
    };

    window.addEventListener("keydown", handleEsc, true);
    return () => window.removeEventListener("keydown", handleEsc, true);
  }, [applyByClickMode, cancelApplyByClick]);

  const footerUI = (
    <>
      {selectedLoad && !applyByClickMode && !applyToSelectedMode && (
        <div className="flex gap-2 justify-center mt-2">
          <button
            onClick={() => {
              unselectRow();
              clearCursorMode();
              setApplyByClickMode(false);
              setApplyToSelectedMode(false);
              setActivationMessage("Selection canceled.");
              setTimeout(() => setActivationMessage(null), 1000);
            }}
            className="px-3 py-1.5 text-xs bg-red-500 text-white rounded hover:bg-red-600"
          >
            Cancel
          </button>

          <button
            onClick={() => {
              const model = useModelStore.getState();
              if (model.clearSelection) model.clearSelection();
              setApplyByClickMode(true);
              setCursorMode("loadcase-apply-click", selectedLoad);
            }}
            className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded"
          >
            Apply by Click
          </button>

          <button
            disabled={
              dataMode === "beam"
                ? useModelStore.getState().selectedBeamIds.length === 0
                : useModelStore.getState().selectedNodeIds.length === 0
            }
            onClick={() => {
              setApplyToSelectedMode(true);
              setActivationMessage(
                `Apply to Selected active → ${dataMode === "beam" ? selectedBeamIds.length : selectedNodeIds.length} ${dataMode} selected`
              );
            }}
            className="px-3 py-1.5 text-xs bg-purple-500 text-white rounded disabled:opacity-50"
          >
            Apply to Selected
          </button>

          <button
            onClick={() => setManualPopupOpen(true)}
            className="px-3 py-1.5 text-xs bg-green-500 text-white rounded"
          >
            Manual Input
          </button>
        </div>
      )}

      {applyByClickMode && (
        <div className="flex gap-2 justify-center mt-2">
          <button
            onClick={() => {
              setApplyByClickMode(false);
              unselectRow();
              clearCursorMode();
              setActivationMessage("Apply by Click canceled.");
              setTimeout(() => setActivationMessage(null), 2000);
            }}
            className="px-3 py-1.5 text-xs bg-red-500 text-white rounded"
          >
            Cancel
          </button>
          <span className="px-3 py-1.5 text-xs text-blue-600">
            Active: {getLoadDisplayName(selectedLoad, dataMode)}
          </span>
        </div>
      )}

      {applyToSelectedMode && (
        <div className="flex gap-2 justify-center mt-2">
          <button
            onClick={() => {
              setApplyToSelectedMode(false);
              unselectRow();
              clearCursorMode();
              setActivationMessage("Apply to Selected canceled.");
              setTimeout(() => setActivationMessage(null), 2000);
            }}
            className="px-3 py-1.5 text-xs bg-red-500 text-white rounded"
          >
            Cancel
          </button>
          <button
            onClick={applyToSelected}
            className="px-3 py-1.5 text-xs bg-purple-500 text-white rounded"
          >
            Apply
          </button>
        </div>
      )}
    </>
  );

  const beamDynamicColumns: TableColumn[] = (() => {
    switch (selectedType) {
      case "all":
        return [
          {
            colname: "Type",
            render: (row) => {
              const typeLabels: Record<string, string> = {
                distributed_uniform: "Dist.1",
                distributed_variable: "Dist.2",
                concentrated_load: "Conc.",
                twisted_load: "Twist",
              };
              return typeLabels[row.type] || row.type || "-";
            },
          },
          {
            colname: "Values",
            render: (row) => {
              if (row.type === "distributed_uniform") {
                return `dx:${row.dx ?? 0}, dy:${row.dy ?? 0}, dz:${row.dz ?? 0}`;
              } else if (row.type === "distributed_variable") {
                return `L(${row.dxLeft ?? 0},${row.dyLeft ?? 0},${row.dzLeft ?? 0}) R(${row.dxRight ?? 0},${row.dyRight ?? 0},${row.dzRight ?? 0})`;
              } else if (row.type === "concentrated_load") {
                return `fx:${row.fx ?? 0}, fy:${row.fy ?? 0}, fz:${row.fz ?? 0}`;
              } else if (row.type === "twisted_load") {
                return `T:${row.torque ?? 0} (${row.torqueDirection || "-"})`;
              }
              return "-";
            },
          },
        ];
      case "distributed_uniform":
        return [
          { colname: "dx", render: (row) => row.dx ?? "-" },
          { colname: "dy", render: (row) => row.dy ?? "-" },
          { colname: "dz", render: (row) => row.dz ?? "-" },
        ];
      case "distributed_variable":
        return [
          { colname: "dx L", render: (row) => row.dxLeft ?? "-" },
          { colname: "dx R", render: (row) => row.dxRight ?? "-" },
          { colname: "dy L", render: (row) => row.dyLeft ?? "-" },
          { colname: "dy R", render: (row) => row.dyRight ?? "-" },
          { colname: "dz L", render: (row) => row.dzLeft ?? "-" },
          { colname: "dz R", render: (row) => row.dzRight ?? "-" },
        ];
      case "concentrated_load":
        return [
          {
            colname: "Ref_Node",
            render: (row) => row.referenceNode ?? "-",
          },
          {
            colname: "Distance",
            render: (row) => row.percentageFromNode ?? "-",
          },
          {
            colname: "fx",
            render: (row) => row.fx ?? "-",
          },
          {
            colname: "fy",
            render: (row) => row.fy ?? "-",
          },
          {
            colname: "fz",
            render: (row) => row.fz ?? "-",
          },
        ];
      case "twisted_load":
        return [
          {
            colname: "Torque",
            render: (row) => row.torque ?? "-",
          },
          {
            colname: "Direction",
            render: (row) => {
              const dir = row.torqueDirection;
              if (!dir) return "-";
              return dir.charAt(0).toUpperCase() + dir.slice(1);
            },
          },
        ];
      default:
        return [];
    }
  })();

  // Table headers for the main table (kept for reference)
  let _theader: TableColumn[] = [];

  if (dataMode === "beam") {
    _theader = [
      {
        colname: "ID",
        render: (_row, index: number) => index + 1,
      },
      {
        colname: "Beam(s)",
        render: (row, index) => {
          // If editing this load's beam IDs
          if (editingBeamIds?.loadIndex === index) {
            return (
              <input
                type="text"
                autoFocus
                className="w-full h-8 px-2 border rounded text-black text-sm"
                value={editingBeamIds.value}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) =>
                  setEditingBeamIds((prev) =>
                    prev ? { ...prev, value: e.target.value } : prev
                  )
                }
                onBlur={commitBeamIdsEdit}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitBeamIdsEdit();
                  if (e.key === "Escape") setEditingBeamIds(null);
                }}
                placeholder="e.g. 1, 2, 3"
              />
            );
          }

          // Display mode - click to edit (including empty state)
          const displayText =
            Array.isArray(row.beamIds) && row.beamIds.length > 0
              ? row.beamIds.join(", ")
              : "(none)";

          return (
            <span
              className="inline-flex items-center h-8 w-full px-2 bg-gray-50 rounded cursor-text text-black text-sm"
              onClick={(e) => startEditingBeams(e, index, row.beamIds || [])}
            >
              {displayText}
            </span>
          );
        },
      },
      ...beamDynamicColumns,
      {
        colname: "Action",
        render: (row) => (
          <div className="flex">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteLoad(row);
              }}
              className="text-red-500 hover:text-red-700"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ];
  } else {
    _theader = [
      {
        colname: "ID",
        render: (_row: TrussPreset, index: number) => index + 1,
      },
      {
        colname: "Node(s)",
        render: (row: TrussPreset, index: number) => {
          // If editing this preset's nodes
          if (editingNodeIds?.presetIndex === index && dataMode === "node") {
            return (
              <input
                type="text"
                autoFocus
                className="w-full h-8 px-2 border rounded text-black text-sm"
                value={editingNodeIds.value}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) =>
                  setEditingNodeIds((prev) =>
                    prev ? { ...prev, value: e.target.value } : prev
                  )
                }
                onBlur={commitNodeIdsEdit}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitNodeIdsEdit();
                  if (e.key === "Escape") setEditingNodeIds(null);
                }}
                placeholder="e.g. 1, 2, 3"
              />
            );
          }

          // Display mode - click to edit (including empty state)
          const displayText =
            Array.isArray(row.nodeIds) && row.nodeIds.length > 0
              ? row.nodeIds.join(", ")
              : "(none)";

          return (
            <span
              className="inline-flex items-center h-8 w-full px-2 bg-gray-50 rounded cursor-text text-black text-sm"
              onClick={(e) => startEditingNodes(e, index, row.nodeIds || [])}
              title="Click to edit node assignments"
            >
              {displayText}
            </span>
          );
        },
      },

      {
        colname: "Type",
        render: (row: TrussPreset) => formatTrussTypeName(row.type),
      },

      {
        colname: "dx",
        render: (row: TrussPreset) =>
          row.type === "regular"
            ? row.rotationValues.X
            : row.perpendicularValues.X,
      },
      {
        colname: "dy",
        render: (row: TrussPreset) =>
          row.type === "regular"
            ? row.rotationValues.Y
            : row.perpendicularValues.Y,
      },
      {
        colname: "dz",
        render: (row: TrussPreset) =>
          row.type === "regular"
            ? row.rotationValues.Z
            : row.perpendicularValues.Z,
      },
      {
        colname: "Action",
        render: (row: TrussPreset) => (
          <div className="flex">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteNodePreset(row);
              }}
              className="text-red-500 hover:text-red-700"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ];
  }

  // Filters row UI (same as before)
  const filtersBar = (
    <div className="px-2 py-2 flex gap-2 items-center bg-gray-50 dark:bg-gray-900 border-b">
      {/* Data Mode Selector */}
      <select
        value={dataMode}
        onChange={(e) => setDataMode(e.target.value as "beam" | "node")}
        className="flex-1 px-2 py-1.5 text-xs rounded border border-gray-300 bg-white text-gray-800"
      >
        <option value="beam">Beam Loads</option>
        <option value="node">Node Loads</option>
      </select>

      {/* Load Type Selector (Beam) */}
      {dataMode === "beam" ? (
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="flex-1 px-2 py-1.5 text-xs rounded border border-gray-300 bg-white text-gray-800"
        >
          {beamLoadTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      ) : (
        <select
          value={selectedNodeType}
          onChange={(e) =>
            setSelectedNodeType(e.target.value as "regular" | "perpendicular")
          }
          className="flex-1 px-2 py-1.5 text-xs rounded border border-gray-300 bg-white text-gray-800"
        >
          <option value="regular">Regular</option>
          <option value="perpendicular">Perpendicular</option>
        </select>
      )}

      {/* Import Button */}
      <button
        onClick={() => fileInputRef.current?.click()}
        className="p-1 hover:bg-gray-600 rounded"
        title="Import from CSV"
      >
        <ArrowDownOnSquareIcon className="h-5 w-5" />
      </button>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelected}
        accept=".csv,.xlsx,.xls"
        style={{ display: "none" }}
      />

      {/* Export Button */}
      <button
        onClick={handleExport}
        className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
        title="Export to CSV"
      >
        <ArrowUpOnSquareIcon className="h-5 w-5" />
      </button>

      {/* Copy Button */}
      <button
        onClick={handleCopy}
        className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
        title="Copy to Clipboard"
      >
        <ClipboardDocumentIcon className="h-5 w-5" />
      </button>

      {/* Add Button - STANDARDIZED */}
      <button
        onClick={() => {
          if (!workingLoadcase) {
            setActivationMessage("Please expand a loadcase panel first to add loads");
            setTimeout(() => setActivationMessage(null), 2500);
            return;
          }
          setPanelVisible(true);
          setIsDirectMode(true);
        }}
        className={`px-3 py-1.5 text-xs rounded flex items-center gap-1 ${
          workingLoadcase
            ? "bg-blue-500 text-white hover:bg-blue-600"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
        }`}
        title={
          workingLoadcase
            ? `Add load to "${workingLoadcase}"`
            : "Expand a loadcase panel first"
        }
      >
        <PlusIcon className="h-4 w-4" />
      </button>
    </div>
  );

  // Helper to get loadcase-specific data for each group
  const getLoadcaseData = (loadcaseName: string) => {
    if (dataMode === "beam") {
      const beamLoadIds = getBeamLoadIdsForLoadcase(loadcaseName);
      const filtered = beamTrusses.filter((load) =>
        beamLoadIds.includes(load.id)
      );
      // Support "all" type filter
      return selectedType === "all"
        ? filtered
        : filtered.filter((t) => t.type === selectedType);
    } else {
      const nodeLoadIds = getNodeLoadIdsForLoadcase(loadcaseName);
      const filtered = groupedTrussPresets.filter((preset: any) =>
        nodeLoadIds.includes(preset.id)
      );
      // Apply node type filter
      return filtered
        .filter((g: any) => g.type === selectedNodeType)
        .map((g: any) => ({
          id: g.id,
          name: g.presetName || g.name,
          type: g.type,
          nodeIds: Array.isArray(g.nodeIds) ? g.nodeIds.flat() : [],
          rotationValues: {
            X: g.rotationValues?.X ?? 0,
            Y: g.rotationValues?.Y ?? 0,
            Z: g.rotationValues?.Z ?? 0,
          },
          perpendicularValues: {
            X: g.perpendicularValues?.X ?? 0,
            Y: g.perpendicularValues?.Y ?? 0,
            Z: g.perpendicularValues?.Z ?? 0,
          },
        }));
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header with Add LoadCase button */}
      <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Loadcase Details
        </h3>
        <button
          onClick={handleCreateLoadcaseGroup}
          className="px-2 py-1 text-xs border border-gray-400 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
          title={`Add ${getNextLoadcaseName()}`}
        >
          Add LoadCase
        </button>
      </div>

      {/* Loadcase List - Collapsible Panels */}
      <div className="flex-1 overflow-y-auto overflow-x-auto">
        {sortedLoadcases.length === 0 ? (
          <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
            <p className="mb-2">No loadcases created.</p>
            <p>Click "Add LoadCase" to create {getNextLoadcaseName()}.</p>
          </div>
        ) : (
          <div>
            {sortedLoadcases.map((group) => {
              const isSelected = activeLoadcaseName === group.name;
              const loadcaseData = getLoadcaseData(group.name);

              return (
                <div
                  key={group.id}
                  className="border-b border-gray-200 dark:border-gray-700"
                >
                  <CollapsiblePanel
                    title={
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-semibold truncate">
                          {group.name}
                        </span>
                      </div>
                    }
                    defaultExpanded={false}
                    isExpanded={expandedPanels.has(group.name)}
                    onToggle={(expanded) => {
                      if (expanded) {
                        // Accordion behavior: close all other panels and open this one
                        setExpandedPanels(new Set([group.name]));
                        setWorkingLoadcase(group.name);
                      } else {
                        // Close this panel
                        setExpandedPanels(new Set());
                        setWorkingLoadcase(null);
                      }
                    }}
                    showApplyButton={true}
                    isApplied={isSelected}
                    onApplyToggle={() => {
                      if (isSelected) {
                        setActiveLoadcase(null);
                      } else {
                        setActiveLoadcase(group.name);
                      }
                    }}
                    headerActions={
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteLoadcaseGroup(group.name);
                        }}
                        className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-500"
                        title="Delete Loadcase"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    }
                  >
                    <TableLayout
                      title=""
                      theader={_theader}
                      tdata={loadcaseData}
                      filters={filtersBar}
                      onRowClick={(row: any) => {
                        cancelAllApplyModes();
                        disableAllModes();
                        if (selectedLoad?.id === row.id) {
                          unselectRow();
                          setApplyByClickMode(false);
                          setApplyToSelectedMode(false);
                          setActivationMessage("Load deselected");
                        } else {
                          selectRow(row.id, row);
                          setApplyByClickMode(false);
                          setApplyToSelectedMode(false);
                          setActivationMessage(
                            `Selected Load: ${getLoadDisplayName(row, dataMode)}. Choose an assignment mode.`
                          );
                        }
                        setTimeout(() => setActivationMessage(null), 1000);
                      }}
                      getRowClassName={(row: any) => {
                        const isRowSelected =
                          globalActiveTable === "loadcases" &&
                          globalActiveRowId === row.id;
                        return isRowSelected
                          ? "selected-table-row bg-blue-50 dark:bg-gray-700"
                          : "";
                      }}
                    />
                  </CollapsiblePanel>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer UI for selected load */}
      {globalActiveTable === "loadcases" && selectedLoad && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-2 bg-gray-50 dark:bg-gray-800">
          {footerUI}
        </div>
      )}

      {/* Activation Message */}
      {activationMessage && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-blue-600 text-white px-6 py-3 rounded-lg shadow-xl">
          <p className="text-sm font-medium">{activationMessage}</p>
        </div>
      )}

      {/* Predefined Load Manager */}
      {PanelVisible && (
        <PredefinedLoadManager
          beamIds={selectedBeamIds}
          inlineMode={null}
          directMode={isDirectMode}
          setDirectMode={setIsDirectMode}
          onClose={() => setPanelVisible(false)}
        />
      )}

      {/* Manual Load Input */}
      {manualPopupOpen && (
        <ManualLoadInput
          onClose={() => setManualPopupOpen(false)}
          onApply={(ids) => applyManual(selectedLoad, ids)}
          mode={dataMode}
          loadName={getLoadDisplayName(selectedLoad, dataMode)}
          existingIds={
            dataMode === "beam"
              ? Array.isArray(selectedLoad?.beamIds)
                ? selectedLoad.beamIds
                : []
              : Array.from(
                  new Set(
                    useTrussStore
                      .getState()
                      .nodeTrusses.filter(
                        (t: any) =>
                          t.presetName?.toLowerCase() ===
                          selectedLoad?.name?.toLowerCase()
                      )
                      .flatMap((t: any) => t.nodeIds || [])
                  )
                )
          }
        />
      )}
    </div>
  );
};
